/**
 * App-wide currency formatting.
 *
 * Coach AI prices everything in AUD. Nothing in the app should render a
 * currency symbol directly — always go through these helpers so the symbol
 * and locale stay consistent (A$) regardless of the visitor's browser locale.
 */

/** The only currency the app charges in today. */
export const APP_CURRENCY = "AUD";

/** Locale used for formatting so AUD renders as "A$" rather than a bare "$". */
const FORMAT_LOCALE = "en-US";

function formatter(currency: string, fractionDigits: number): Intl.NumberFormat {
  return new Intl.NumberFormat(FORMAT_LOCALE, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

/**
 * Formats a whole-unit amount (e.g. 149 → "A$149"), keeping cents only when
 * the amount actually has them.
 */
export function formatMoney(
  amount: number | string | null | undefined,
  currency: string = APP_CURRENCY,
): string {
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (value == null || !Number.isFinite(value)) return "";
  const hasCents = Math.round(value * 100) % 100 !== 0;
  try {
    return formatter(currency, hasCents ? 2 : 0).format(value);
  } catch {
    return `A$${hasCents ? value.toFixed(2) : Math.round(value)}`;
  }
}

/** Formats an amount held in cents (e.g. 2700 → "A$27"). */
export function formatMoneyCents(
  cents: number | null | undefined,
  currency: string = APP_CURRENCY,
): string {
  if (cents == null || !Number.isFinite(cents)) return "";
  return formatMoney(cents / 100, currency);
}
